'use client';

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  useGetFieldDefinitionsQuery,
  useGetObjectDefinitionsListQuery,
} from 'src/api/redux';
import { useGetAllStrategiesFilterOptionsQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { useDebounce } from 'use-debounce';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

interface Status {
  id: string;
  name: string;
  crm_type?: string;
  crm_status?: string;
}


const StrategyStatusDropdown: React.FC = (): React.ReactElement => {
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedStatuses,
    setSelectedStatuses,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [allStatuses, setAllStatuses] = useState<Status[]>([]);
  const isLoadingNextPage = useRef<boolean>(false);

  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery();
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
      {
        objectId: objectDefinitions?.results?.[0]?.id,
      },
      {
        skip: !objectDefinitions?.results?.[0]?.id,
      }
    );

  // Create a lookup map from status value to label from field definitions
  const statusLabelMap = useMemo(() => {
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [fieldDefinitions]);

  const { data: statuses, isFetching } = useGetAllStrategiesFilterOptionsQuery(
    {
      type: 'crm-status',
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch,
      page,
      pageSize,
    },
    { skip: selectedChainIds.length === 0 }
  );

  const hasMore = statuses?.results?.length === pageSize;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  useEffect(() => {
    setPage(1);
    setAllStatuses([]);
    isLoadingNextPage.current = false;
  }, [debouncedSearch, selectedChainIds, boundedTerritory?.id, selectedTerritory?.id]);

  useEffect(() => {
    if (statuses?.results) {
      setAllStatuses((prev) => {
        const validStatuses = statuses.results
          .filter((s: any) => (s.id && s.name) || (s.crm_type && s.crm_status))
          .map((status: any) => {
            // Handle new format (id, name) - look up label from field definitions
            if (status.id && status.name) {
              const label = statusLabelMap.get(status.name) || status.name;
              return {
                id: status.id,
                name: label,
                crm_type: status.crm_type,
                crm_status: status.crm_status,
              };
            }
            // Handle old format (crm_type, crm_status)
            const label =
              statusLabelMap.get(status.crm_status) ||
              `${status.crm_type} - ${status.crm_status}`;
            return {
              id: `${status.crm_type}-${status.crm_status}`,
              name: label,
              crm_type: status.crm_type,
              crm_status: status.crm_status,
            };
          });

        if (page === 1) {
          isLoadingNextPage.current = false;
          return validStatuses;
        }
        const existingIds = new Set(prev.map((s: Status) => s.id));
        const newStatuses = validStatuses.filter(
          (s: Status) => !existingIds.has(s.id)
        );
        isLoadingNextPage.current = false;
        return [...prev, ...newStatuses];
      });
    }
  }, [statuses, page, statusLabelMap]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasMore || isLoadingNextPage.current)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingNextPage.current = true;
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;

    if (!isOpen) {
      calculatePosition();
    }

    setIsOpen(!isOpen);
  };

  const handleStatusToggle = (statusId: string) => {
    const updatedStatuses = selectedStatuses.includes(statusId)
      ? selectedStatuses.filter((s) => s !== statusId)
      : [...selectedStatuses, statusId];
    setSelectedStatuses(updatedStatuses);
  };

  const handleClearStatuses = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStatuses([]);
  };

  const getDisplayText = () => {
    if (selectedStatuses.length === 0) return 'Statuses';
    if (selectedStatuses.length === 1) {
      return (
        allStatuses.find((s) => s.id === selectedStatuses[0])?.name ||
        selectedStatuses[0]
      );
    }
    return `${selectedStatuses.length} Statuses`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faCircle}
        iconClassName="text-tertiary text-[9px]"
        label="Status"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={selectedStatuses.length > 0}
        onClear={handleClearStatuses}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-status-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search status..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={selectedStatuses.length}
        onClearAll={() => setSelectedStatuses([])}
      >
        {allStatuses.map((status) => (
          <CheckboxListItem
            key={status.id}
            id={status.id}
            label={status.name}
            isSelected={selectedStatuses.includes(status.id)}
            onToggle={handleStatusToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={allStatuses.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="statuses"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategyStatusDropdown;
